// Complete App.js with Hash Routing and About Page
import React, { useState, useEffect, useRef } from 'react';
import './App.css';

// About Page Component
function AboutPage({ onBack, darkMode = false }) {
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: darkMode ? '#1a1a2e' : '#f8f9fa',
      color: darkMode ? '#ffffff' : '#333333',
      transition: 'all 0.3s ease',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    nav: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem 2rem',
      borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)'
    },
    navTitle: {
      fontSize: '1.2rem',
      fontWeight: '700',
      color: darkMode ? '#ffffff' : '#333333'
    },
    backButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#4f8cff',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontWeight: '600',
      fontSize: '0.9rem',
      cursor: 'pointer',
      textDecoration: 'none'
    },
    header: {
      textAlign: 'center',
      padding: '2rem 1rem'
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      margin: '0 0 1rem 0',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '1rem 2rem 3rem'
    },
    profileSection: {
      textAlign: 'center',
      marginBottom: '3rem'
    },
    profileImage: {
      width: '200px',
      height: '200px',
      borderRadius: '50%',
      objectFit: 'cover',
      border: darkMode ? '4px solid rgba(255, 255, 255, 0.2)' : '4px solid rgba(0, 0, 0, 0.1)',
      boxShadow: darkMode ? '0 8px 32px rgba(255, 255, 255, 0.1)' : '0 8px 32px rgba(0, 0, 0, 0.15)',
      marginBottom: '1.5rem'
    },
    name: {
      fontSize: '2rem',
      fontWeight: '600',
      margin: '0 0 0.5rem 0'
    },
    subtitle: {
      fontSize: '1.1rem',
      opacity: '0.8',
      margin: '0'
    },
    section: {
      background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
      border: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
      borderRadius: '16px',
      padding: '2rem',
      marginBottom: '2rem'
    },
    sectionTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      marginBottom: '1rem',
      color: '#4f8cff'
    },
    paragraph: {
      fontSize: '1.1rem',
      lineHeight: '1.6',
      marginBottom: '1rem',
      marginTop: '0.5rem',
      opacity: '0.9',
    },
    highlight: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      fontWeight: '600'
    },
    stats: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginTop: '2rem'
    },
    statBox: {
      background: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
      border: darkMode ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.1)',
      borderRadius: '12px',
      padding: '1.5rem',
      textAlign: 'center',
      marginBottom: '1rem',
    },
    statEmoji: {
      fontSize: '2rem',
      marginBottom: '0.5rem'
    },
    statTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      marginBottom: '0.25rem'
    },
    statValue: {
      fontSize: '0.9rem',
      opacity: '0.8'
    }
  };

  return (
    <div style={styles.container}>
      {/* Navigation */}
      <nav style={styles.nav}>
        <div style={styles.navTitle}>
          🧠 BrainRot Study
        </div>
        <button 
          style={styles.backButton}
          onClick={onBack}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#3a7bd5'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#4f8cff'}
        >
          ← Back to App
        </button>
      </nav>

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>About BrainRot Study</h1>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Profile Section */}
        <div style={styles.profileSection}>
          <img
            src="../headshot.png"
            alt="Creator"
            style={styles.profileImage}
          />
          <h2 style={styles.name}>Thomas "Max" Register</h2>
          <p style={styles.subtitle}>Creator of BrainRot Study • Student • Developer</p>
        </div>

        {/* Story Section */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>🚀 Why I Built This</h3>
          <p style={styles.paragraph}>
            I was that student who could binge-watch 8 hours of TikTok compilations but couldn't focus on a textbook for 10 minutes. 
            Sound familiar? After failing my third exam while simultaneously becoming a <span style={styles.highlight}>Subway Surfers speedrun expert</span>, 
            I realized something had to change.
          </p>
          <p style={styles.paragraph}>
            The problem wasn't that I couldn't focus — it was that traditional studying felt like torture compared to the 
            instant gratification of endless scroll content. So I thought: <span style={styles.highlight}>what if we could hack our own dopamine addiction?</span>
          </p>
          <p style={styles.paragraph}>
            BrainRot Study was born from pure frustration and a lot of late-night coding sessions fueled by energy drinks and 
            the realization that <span style={styles.highlight}>Gen Z brains work differently</span>. We're wired for multitasking, 
            rapid visual processing, and constant stimulation. Instead of fighting it, let's use it.
          </p>
          <p style={styles.paragraph}>
            This app combines AI-generated flashcards with the hypnotic power of background gameplay videos. 
            It's not just a study tool — it's a <span style={styles.highlight}>dopamine optimization system</span> disguised as an educational app.
          </p>
        </div>

        {/* Fun Stats */}
        <div style={styles.stats}>
          <div style={styles.statBox}>
            <div style={styles.statEmoji}>📚</div>
            <div style={styles.statTitle}>Questions Generated</div>
            <div style={styles.statValue}>10,000+ and counting</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statEmoji}>🎮</div>
            <div style={styles.statTitle}>Hours of Brainrot</div>
            <div style={styles.statValue}>Probably too many</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statEmoji}>🧠</div>
            <div style={styles.statTitle}>Dopamine Hits</div>
            <div style={styles.statValue}>Infinite</div>
          </div>
        </div>

        {/* Final Thoughts */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>💭 The Real Talk</h3>
          <p style={styles.paragraph}>
            Look, I'm not saying this will magically turn you into a straight-A student overnight. But if you're someone who 
            struggles with traditional study methods, feels guilty about your phone addiction, or just wants to try something different — 
            this might be your jam.
          </p>
          <p style={styles.paragraph}>
            We're living in the age of 8-second attention spans and infinite scroll. Instead of pretending that's not our reality, 
            let's <span style={styles.highlight}>embrace the chaos and make it work for us</span>.
          </p>
          <p style={styles.paragraph}>
            Happy studying, and may your dopamine levels stay optimally chaotic. 🚀
          </p>
        </div>
      </div>
    </div>
  );
}

// Local storage key for saving questions
const LOCAL_STORAGE_KEY = "brainrot2study_questions";

// Request Gemini to return Q: and then the question, and A: and then the answer in CSV format
async function fetchGeminiQuestions(syllabus, numQuestions) {
  const prompt = `Create ${numQuestions} Quizlet-style flashcard questions and answers in this exact CSV format (no markdown, no JSON, just plain text):\nQ: <question 1>\nA: <answer 1>\nQ: <question 2>\nA: <answer 2>\n...and so on, based on this syllabus or notes:\n${syllabus}`;
  console.log('[fetchGeminiQuestions] Prompt sent to Gemini:', prompt);

  const response = await fetch('/gemini', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ syllabus, numQuestions })
  });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(`[fetchGeminiQuestions] Gemini API error: ${response.statusText}`);
  }

  console.log('[fetchGeminiQuestions] Raw Gemini response:', data);

  // Try to extract the model's text response
  const text = data.candidates?.[0]?.content?.parts?.[0]?.text || "";
  return text;
}

// Helper to extract Q/A pairs from Gemini's CSV/plaintext response
function extractQuestionsFromGeminiCSV(geminiText) {
  // Remove any commas that are used as separators between Q/A pairs
  const cleaned = geminiText.replace(/,\s*Q:/g, '\nQ:');
  const lines = cleaned
    .replace(/\r/g, '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  const questions = [];
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].startsWith('Q:')) {
      const q = lines[i].slice(2).trim();
      let a = '';
      // Look ahead for the next A: line
      if (i + 1 < lines.length && lines[i + 1].startsWith('A:')) {
        a = lines[i + 1].slice(2).trim();
        i++; // skip answer line
      }
      // Only add if question is not empty
      if (q) {
        questions.push({
          q,
          a
        });
      }
    }
  }
  return questions;
}

export default function BrainrotStudyApp() {
  const [syllabus, setSyllabus] = useState('');
  const [questions, setQuestions] = useState([]); // [{q, a}]
  const [loading, setLoading] = useState(false);
  const [videoChoice, setVideoChoice] = useState('subway');
  const [darkMode, setDarkMode] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [numQuestions, setNumQuestions] = useState(5);
  const [studyStart, setStudyStart] = useState(null);
  const [timer, setTimer] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');
  
  // Dopamine reader state
  const [dopamineLevel, setDopamineLevel] = useState(50);
  const [blownUp, setBlownUp] = useState(false);
  
  const timerRef = useRef();

  // Hash routing logic
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove the #
      setCurrentPage(hash || 'home');
    };

    // Set initial page based on current hash
    handleHashChange();
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Functions to change page and update URL
  const navigateToAbout = () => {
    window.location.hash = 'about';
    setCurrentPage('about');
  };

  const navigateToHome = () => {
    window.location.hash = '';
    setCurrentPage('home');
  };

  // Timer logic
  useEffect(() => {
    if (studyStart !== null) {
      timerRef.current = setInterval(() => {
        setTimer(Math.floor((Date.now() - studyStart) / 1000));
      }, 1000);
    } else {
      setTimer(0);
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [studyStart]);

  // Dopamine level animation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setDopamineLevel(prev => {
        const newLevel = prev + (Math.random() - 0.5) * 10;
        const clampedLevel = Math.max(0, Math.min(100, newLevel));
        
        // Check if dopamine level hits 100%
        if (clampedLevel >= 100 && !blownUp) {
          setBlownUp(true);
          setTimeout(() => setBlownUp(false), 3000); // Reset after 3 seconds
        }
        
        return clampedLevel;
      });
    }, 500);

    return () => clearInterval(interval);
  }, [blownUp]);

  // Load questions from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setQuestions(parsed);
          setCurrentIdx(0);
          setStudyStart(Date.now());
          console.log('[localStorage] Loaded questions from localStorage:', parsed);
        }
      } catch (e) {
        console.warn('[localStorage] Failed to parse stored questions:', e);
      }
    }
  }, []);

  // Save questions to localStorage whenever they change
  useEffect(() => {
    if (questions.length > 0) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(questions));
      console.log('[localStorage] Saved questions to localStorage:', questions);
    }
  }, [questions]);

  // Generate questions from syllabus using Gemini
  const generateQuestions = async () => {
    setLoading(true);
    try {
      console.log('[generateQuestions] Syllabus input:', syllabus);
      const geminiText = await fetchGeminiQuestions(syllabus, numQuestions);

      // Extract Q/A pairs from Gemini's CSV/plaintext response
      let arr = extractQuestionsFromGeminiCSV(geminiText);
      console.log('[generateQuestions] Extracted questions array:', arr);

      setQuestions(arr);
      setCurrentIdx(0);
      setStudyStart(Date.now());
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(arr));
      console.log('[generateQuestions] Final questions array set in state and localStorage:', arr);
    } catch (err) {
      console.error('[generateQuestions] Error fetching questions:', err);
    }
    setLoading(false);
  };

  // Move to next question
  const handleNext = () => {
    setShowAnswer(false); // Hide answer when moving to next question
    if (currentIdx < questions.length - 1) {
      setCurrentIdx(currentIdx + 1);
    } else {
      setStudyStart(null); // Stop timer
    }
    
    // Boost dopamine when answering questions
    setDopamineLevel(prev => Math.min(100, prev + 15));
  };

  // Timer display
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const videoMap = {
    subway: 'L_fcrOyoWZ8',
    minecraft: 'n_Dv4JMiwK8',
  };

  const theme = darkMode ? 'dark' : 'light';

  // Show About page if URL is #about
  if (currentPage === 'about') {
    return <AboutPage onBack={navigateToHome} darkMode={darkMode} />;
  }

  return (
    <>
      <div className={`app-container ${theme}`}>
        {/* Navigation */}
        <nav style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 2rem',
          borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            fontSize: '1.2rem',
            fontWeight: '700',
            color: darkMode ? '#ffffff' : '#333333'
          }}>
            🧠 BrainRot Study
          </div>
          <a 
            href="#about"
            onClick={(e) => {
              e.preventDefault();
              navigateToAbout();
            }}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#4f8cff',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              fontWeight: '600',
              fontSize: '0.9rem'
            }}
          >
            About
          </a>
        </nav>

        {/* Top half: Study */}
        <div className="study-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h1 className="title">🧠 BrainRot Fueled Studying 🔥</h1>
          {/* Only show textarea and dropdown if no questions yet */}
          {questions.length === 0 ? (
            <>
              <textarea
                placeholder="Paste your class study guides, notes, or syllabus here... We'll turn that chaos into meaningful study questions."
                value={syllabus}
                onChange={(e) => {
                  setSyllabus(e.target.value);
                }}
                className="syllabus-input"
                rows={Math.max(3, Math.min(10, syllabus.split('\n').length))}
                cols={Math.max(20, Math.min(80, Math.ceil(syllabus.length / 10)))}
                style={{ resize: 'vertical' }}
              />
              <div style={{ margin: '12px 0' }}>
                <label htmlFor="numQuestions" style={{ marginRight: 8 }}>Number of questions:</label>
                <select
                  id="numQuestions"
                  value={numQuestions}
                  onChange={e => setNumQuestions(Number(e.target.value))}
                  style={{ fontSize: '1rem', padding: '4px 8px' }}
                >
                  {[3, 5, 10, 15, 20].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <button
                className="generate-button"
                onClick={() => {
                  console.log('[UI] Generate Questions button clicked.');
                  generateQuestions();
                }}
                disabled={loading || !syllabus.trim()}
                style={{  display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              >
                {loading ? 'Generating...' : 'Generate Questions'}
              </button>
            </>
          ) : (
            // Show one question at a time (currentIdx)
            <div className="flashcard" style={{ width: 640, maxWidth: '90vw', marginBottom: 24 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                Study Timer: {formatTime(timer)}
              </div>
              <div><b>Q:</b> {questions[currentIdx].q}</div>
              <details
                style={{marginTop: '1em'}}
                open={showAnswer}
                onClick={e => {
                  if (!showAnswer) setShowAnswer(true);
                  e.preventDefault();
                }}
              >
                <summary>Show Answer</summary>
                {showAnswer && <div><b>A:</b> {questions[currentIdx].a}</div>}
              </details>
              <div style={{marginTop: '1em'}}>
                {currentIdx < questions.length - 1 ? (
                  <button onClick={handleNext} style={{marginRight: 8}}>Next Question</button>
                ) : (
                  <div style={{fontWeight: 'bold', color: '#4f8cff', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                    Done Studying! 🎉
                    <button
                      className="generate-button"
                      style={{ marginTop: 16 }}
                      onClick={() => {
                        // Shuffle questions and reset state for another round
                        const shuffled = [...questions].sort(() => Math.random() - 0.5);
                        setQuestions(shuffled);
                        setCurrentIdx(0);
                        setShowAnswer(false);
                      }}
                    >
                      Restart & Shuffle Questions
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="toggles">
            {/* Video toggle switch */}
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={videoChoice === 'minecraft'}
                onChange={() => {
                  const newChoice = videoChoice === 'minecraft' ? 'subway' : 'minecraft';
                  setVideoChoice(newChoice);
                  console.log('[UI] Video choice toggled:', newChoice);
                }}
                aria-checked={videoChoice === 'minecraft'}
              />
              <span className="slider-switch" />
              <span className="switch-label">
                {videoChoice === 'minecraft' ? '🟥 Subway Surfers' : '🟩 Minecraft'}
              </span>
            </label>
            {/* Dark mode toggle switch */}
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={darkMode}
                onChange={() => {
                  setDarkMode((prev) => {
                    const newMode = !prev;
                    console.log('[UI] Dark mode toggled:', newMode);
                    return newMode;
                  });
                }}
                aria-checked={darkMode}
              />
              <span className="slider-switch" />
              <span className="switch-label">{darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}</span>
            </label>
            {/* Always show Reset button */}
            <button
              className="generate-button"
              style={{ marginLeft: 46, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              onClick={() => {
                setQuestions([]);
                setCurrentIdx(0);
                setSyllabus('');
                setStudyStart(null);
                setTimer(0);
                setShowAnswer(false);
                localStorage.removeItem(LOCAL_STORAGE_KEY);
              }}
            >
              Reset Questions
            </button>
          </div>
        </div>
        
        {/* Bottom half: Brainrot Video */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div className="video-container">
            <iframe
              title="Brainrot Video"
              src={`https://www.youtube-nocookie.com/embed/${videoMap[videoChoice]}?autoplay=1&loop=1&playlist=${videoMap[videoChoice]}&controls=0&modestbranding=1&rel=0&disablekb=1`}
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen
              sandbox="allow-same-origin allow-scripts"
            ></iframe>
          </div>
        </div>
      </div>
        
      {/* Affiliate Links Section */}
      <div className="affiliate-section" style={{ padding: '2rem 1rem', maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ 
          display: 'flex', 
          gap: '1.5rem', 
          flexWrap: 'wrap', 
          justifyContent: 'center'
        }}>
          
          {/* Box 1 */}
          <div className="affiliate-box" style={{
            background: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            border: darkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '12px',
            padding: '1.5rem',
            textAlign: 'center',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            cursor: 'pointer',
            flex: '1 1 250px',
            minWidth: '250px',
            maxWidth: '300px'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-4px)';
            e.target.style.boxShadow = darkMode ? '0 8px 25px rgba(255, 255, 255, 0.1)' : '0 8px 25px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📚</div>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', fontWeight: '600' }}>Study Resources</h3>
            <p style={{ margin: '0', fontSize: '0.9rem', opacity: '0.8' }}>Premium study tools & guides on Amazon</p>
            <a href="https://amzn.to/3T5kbNW" style={{ 
              display: 'inline-block', 
              marginTop: '1rem', 
              padding: '0.5rem 1rem', 
              background: '#4f8cff', 
              color: 'white', 
              textDecoration: 'none', 
              borderRadius: '6px',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}>Shop Now</a>
          </div>

          {/* Box 3 */}
          <div className="affiliate-box" style={{
            background: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            border: darkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '12px',
            padding: '1.5rem',
            textAlign: 'center',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            cursor: 'pointer',
            flex: '1 1 250px',
            minWidth: '250px',
            maxWidth: '300px'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-4px)';
            e.target.style.boxShadow = darkMode ? '0 8px 25px rgba(255, 255, 255, 0.1)' : '0 8px 25px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚡</div>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', fontWeight: '600' }}>Brain Boosters</h3>
            <p style={{ margin: '0', fontSize: '0.9rem', opacity: '0.8' }}>Supplements & productivity tools on Amazon</p>
            <a href="https://amzn.to/4jyqGUe" style={{ 
              display: 'inline-block', 
              marginTop: '1rem', 
              padding: '0.5rem 1rem', 
              background: '#4f8cff', 
              color: 'white', 
              textDecoration: 'none', 
              borderRadius: '6px',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}>Get Boosted</a>
          </div>
          
        </div>
      </div>

      {/* Commentary Section */}
      <div className="commentary-section" style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto' }}>
        <h2>📺 Why Study with Subway Surfers Is the Move</h2>
        <p>
          Let's be honest: if you've ever stared at a wall for 3 hours instead of reviewing your chemistry notes,
          you're not alone. According to a Microsoft study, our average attention span is now shorter than that of
          a goldfish — 8 seconds. But looping gameplay and rapid visuals (aka TikTok brainrot) stimulate your
          reward system, keeping your brain alert. This app weaponizes that brainrot for good. We're not fighting
          your dopamine addiction — we're monetizing it for A+ grades.
        </p>
        <h2>🧠 Flashcards So Smart They Could Teach the Class</h2>
        <p>
          Powered by the same AI that's acing the bar exam and writing Shakespearean sonnets about spaghetti code,
          these flashcards are generated straight from your syllabus. No more "uhh what was that again?" moments.
          They're study tools, focus hacks, and your new digital tutor all in one. ADHD study tools, AI flashcard
          apps, and Gen Z microlearning solutions unite here.
        </p>
        <h2>💥 You Bring the Chaos. We Bring the Structure.</h2>
        <p>
          This isn't just study support — it's a dopamine-optimized, multitasking-friendly, high-stimulation split
          screen system. Whether you're jamming on biology terms while watching Minecraft parkour or flipping
          flashcards during Subway Surfers grind sessions, Brainrot2Study is built for overstimulated,
          high-achieving chaos agents like you.
        </p>
      </div>
    
      {/* Dopamine Meter Section */}
      <div className="student-section" style={{ 
        marginTop: '2rem', 
        padding: '2rem', 
        textAlign: 'center', 
        background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)', 
        borderRadius: '8px',
        maxWidth: '900px',
        margin: '2rem auto 0'
      }}>
        <h2>🏆 You made it to the bottom. Your reward? A dopamine meter, obviously.</h2>
        <div className="dopamine-meter">
          <h3>🧪 Dopamine Meter</h3>
          {blownUp ? (
            <img
              src="https://media.tenor.com/Cn7xB7U6M5EAAAAd/explosion-boom.gif"
              alt="Explosion"
              style={{ width: '160px', borderRadius: '8px' }}
            />
          ) : dopamineLevel < 40 ? (
            <img
              src="https://media2.giphy.com/media/v1.Y2lkPTc5MGI3NjExNDloaGViaXJkMHB3emFqOXgxa3NzOTY0cjJiejE1aXQ5c2FlMGZuYiZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/l1AsyjZ8XLd1V7pUk/giphy.gif"
              alt="Low Dopamine Sad"
              style={{ width: '200px', borderRadius: '8px' }}
            />
          ) : dopamineLevel >= 41 && dopamineLevel <= 85 ? (
            <img
              src="https://media2.giphy.com/media/v1.Y2lkPTc5MGI3NjExMGd5ZDNwOWc0bjFtMm9hMmo3N2NwZ2x3amtudjV1aW1oY2NrZ3FlMCZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/WoWm8YzFQJg5i/giphy.gif"
              alt="Medium Dopamine Thinking"
              style={{ width: '200px', borderRadius: '8px' }}
            />
          ) : (
            <img
              src="https://media1.giphy.com/media/v1.Y2lkPTc5MGI3NjExOWtxNDlucWJvMWVvaWM1dzRhZmd6d3l2bXYya2lpYW0xbHNqeHFxNSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/SKGo6OYe24EBG/giphy.gif"
              alt="High Dopamine Happy"
              style={{ width: '200px', borderRadius: '8px' }}
            />
          )}
          <div style={{ marginTop: '0.5rem' }}>Dopamine Level: {Math.round(dopamineLevel)}%</div>
          <div style={{ 
            height: '10px', 
            width: '200px', 
            background: '#ccc', 
            borderRadius: '5px', 
            overflow: 'hidden', 
            margin: '0.5rem auto' 
          }}>
            <div
              style={{
                width: `${dopamineLevel}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #ff8a00, #e52e71)',
                transition: 'width 0.4s ease-in-out'
              }}
            ></div>
          </div>
          {blownUp && <p style={{ color: 'red', fontWeight: 'bold' }}>💥 Dopamine overload! You just leveled up your neurons.</p>}
        </div>
      </div>
    </>
  );
}
             